import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, Download, Trash2, Loader2, Inbox } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const ACTION_BADGE = {
  created: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  updated: 'bg-blue-100 text-blue-700 border-blue-300',
  skipped: 'bg-amber-100 text-amber-700 border-amber-300',
};

const FormSubmissions = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/forms/${formId}/submissions`, { headers: getAuthHeaders() });
      setForm(res.data.form);
      setSubmissions(res.data.submissions);
    } catch {
      toast.error('Failed to load submissions');
      navigate('/admin/retreat/forms');
    } finally {
      setLoading(false);
    }
  }, [formId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/forms/${formId}/submissions/export`, {
        headers: getAuthHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(form?.title || 'form').replace(/\s+/g, '_')}_submissions.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const deleteSubmission = async (sub) => {
    if (!window.confirm('Delete this submission? (The waitlist entry it created is NOT removed.)')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/retreat/forms/${formId}/submissions/${sub.id}`, { headers: getAuthHeaders() });
      toast.success('Submission deleted');
      setSubmissions(subs => subs.filter(s => s.id !== sub.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const answerText = (sub, field) => {
    const v = (sub.answers || {})[field.id];
    if (Array.isArray(v)) return v.join(', ');
    return v || '—';
  };

  if (loading || !form) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-wine" /></div>;
  }

  return (
    <div className="p-4 md:p-8 max-w-full" data-testid="form-submissions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/retreat/forms')} className="-ml-3 mb-2" data-testid="back-to-forms-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> All Forms
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Inbox className="h-6 w-6 text-wine" /> {form.title} — Submissions
          </h1>
          <p className="text-sm text-gray-500 mt-1">{submissions.length} submission{submissions.length === 1 ? '' : 's'}</p>
        </div>
        <Button onClick={exportCsv} disabled={exporting || submissions.length === 0} variant="outline" data-testid="export-submissions-btn">
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      {submissions.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-gray-500">
          No submissions yet. Responses will appear here as soon as someone fills out this form.
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left font-semibold text-gray-600 px-4 py-3 whitespace-nowrap">Submitted</th>
                  {form.fields.map(f => (
                    <th key={f.id} className="text-left font-semibold text-gray-600 px-4 py-3 whitespace-nowrap max-w-[220px] truncate">{f.label}</th>
                  ))}
                  <th className="text-left font-semibold text-gray-600 px-4 py-3 whitespace-nowrap">Waitlist</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {submissions.map(sub => (
                  <tr key={sub.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`submission-row-${sub.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {new Date(sub.submitted_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </td>
                    {form.fields.map(f => (
                      <td key={f.id} className="px-4 py-3 max-w-[260px] truncate text-gray-800" title={answerText(sub, f)}>
                        {answerText(sub, f)}
                      </td>
                    ))}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${ACTION_BADGE[sub.waitlist_action] || 'bg-gray-100 text-gray-600 border-gray-300'}`}
                        title={sub.waitlist_reason || ''}>
                        {sub.waitlist_action === 'created' ? 'Added' : sub.waitlist_action === 'updated' ? 'Updated' : sub.waitlist_action || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" onClick={() => deleteSubmission(sub)}
                        className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" data-testid={`delete-submission-${sub.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FormSubmissions;
